import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createAppAsyncThunk } from "../../../../app/thunkWithTypes";
import Position from "../../../../api/models/portfolioPosition";
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { upsertPosition as apiUpsertPosition, getAssetTypes, getSecurities, getLastPrice, getSecuritiesByCnpj } from "../../../../api/client";
import { RootState } from "../../../../app/store";
import User from "../../../../api/models/user";
import PortfolioComposite from "../../../../api/models/portfolioComposite";
import Security from "../../../../api/models/fixProtocol/instrument/security";
import AssetType from "../../../../api/models/fixProtocol/instrument/assetType";
import LastPrice from "../../../../api/models/lastPrice";
import DynamicWeightPriceRange from "../../../../api/models/dynamicWeightPriceRange";
import DynamicWeight from "../../../../api/models/dynamicWeight";
import { isFund } from "../../../../app/utils";

interface EditPositionSliceState {
  assetTypes: object[];
  securities: object[];
  position: object | undefined
  parentPosition: object | undefined
  securitySelected: object | undefined
  lastPrice: object | undefined
  fetchAssetTypeStatus: 'idle' | 'pending' | 'succeeded' | 'failed'
  fetchSecurityStatus: 'idle' | 'pending' | 'succeeded' | 'failed'
  fetchLastPriceStatus: 'idle' | 'pending' | 'succeeded' | 'failed'
  upsertStatus: 'idle' | 'pending' | 'succeeded' | 'failed'
}

const initialState: EditPositionSliceState = {
  assetTypes: [],
  securities: [],
  position: undefined,
  parentPosition: undefined,
  securitySelected: undefined,
  lastPrice: undefined,
  fetchAssetTypeStatus: 'idle',
  fetchSecurityStatus: 'idle',
  fetchLastPriceStatus: 'idle',
  upsertStatus: 'idle'
}

export const upsertPosition = createAppAsyncThunk('editPosition/upsert', async (data: { accessToken: string, position: Position, user: User }) => {
  const { accessToken, position, user } = data;
  if (user && position) {
    const id = await apiUpsertPosition(accessToken, user.id, position);
    if (id) {
      position.id = id;
      return instanceToPlain(position);
    }
  } else {
    return undefined;
  }
}, {
  condition(arg, thunkApi) {
    const status = selectUpsertPositionStatus(thunkApi.getState())
    if (status !== 'idle') {
      return false;
    }
  }
})

export const fetchAssetTypes = createAppAsyncThunk('editPosition/fetchAssetTypes', async (data: { accessToken: string }) => {
  const { accessToken } = data;
  const response = await getAssetTypes(accessToken);
  return instanceToPlain(response);
}, {
  condition(arg, thunkApi) {
    const status = selectFetchAssetTypeStatus(thunkApi.getState())
    if (status !== 'idle') {
      return false;
    }
  }
})

export const fetchSecurities = createAppAsyncThunk('editPosition/fetchSecurities', async (data: { accessToken: string, assetTypeIds: string[], symbolFilter: string, cnpjFilter?: string }) => {
  const { accessToken, assetTypeIds, symbolFilter, cnpjFilter } = data;
  const securities: Security[] = [];
  await Promise.all(assetTypeIds.map(async assetTypeId => {
    if (cnpjFilter) {
      securities.push(...await getSecuritiesByCnpj(accessToken, assetTypeId, cnpjFilter));
    } else {
      securities.push(...await getSecurities(accessToken, assetTypeId, symbolFilter));
    }
  }))
  securities.sort((a, b) => a.symbol < b.symbol ? -1 : 1)
  console.log(`securities: ${JSON.stringify(securities)}`)
  return securities;
}, {
  condition(arg, thunkApi) {
    const status = selectFetchSecurityStatus(thunkApi.getState())
    if (status !== 'idle') {
      return false;
    }
  }
})

export const fetchLastPrice = createAppAsyncThunk('editPosition/fetchLastPrice', async (data: { accessToken: string, securityId: string }) => {
  const { accessToken, securityId } = data;
  const lastPrice = await getLastPrice(accessToken, securityId);
  return instanceToPlain(lastPrice);
}, {
  condition(arg, thunkApi) {
    const status = selectFetchLastPriceStatus(thunkApi.getState())
    if (status !== 'idle') {
      return false;
    }
  }
})

const editPositionSlice = createSlice({
  name: 'editPosition',
  initialState,
  reducers: {
    addDynamicWeightPriceRange(state) {
      const position = plainToInstance(Position, state.position);
      const securitySelected = plainToInstance(Security, state.securitySelected);
      const priceStep = isFund(securitySelected?.assetType) ? 1e-7 : 0.01;
      let priceMax = priceStep;
      if (!position.dynamicWeight) {
        position.dynamicWeight = new DynamicWeight(position.id, new Date(), []);
      } else {
        priceMax = (position.dynamicWeight?.weightPriceRanges[position.dynamicWeight?.weightPriceRanges.length - 1].priceMax ?? 0) + priceStep;
      }
      position.dynamicWeight?.weightPriceRanges.push(new DynamicWeightPriceRange(undefined, position.id, priceMax, 1));
      state.position = instanceToPlain(position);
    },
    updateDynamicWeightPriceRange(state, action: PayloadAction<{ arrayIndex: number, dynamicWeightPriceRange: object }>) {
      const arrayIndex = action.payload.arrayIndex;
      const updatedDynamicWeightPriceRange = plainToInstance(DynamicWeightPriceRange, action.payload.dynamicWeightPriceRange);
      if (updatedDynamicWeightPriceRange instanceof DynamicWeightPriceRange) {
        const position = plainToInstance(Position, state.position);
        if (position.dynamicWeight?.weightPriceRanges.length && position.dynamicWeight?.weightPriceRanges.length > arrayIndex) {
          position.dynamicWeight?.weightPriceRanges.splice(arrayIndex, 1, updatedDynamicWeightPriceRange);
          let lastPriceMax = updatedDynamicWeightPriceRange.priceMax;
          const securitySelected = plainToInstance(Security, state.securitySelected);
          const priceStep = isFund(securitySelected.assetType) ? 1e-7 : 0.01;
          for (let index = arrayIndex + 1; index < position.dynamicWeight?.weightPriceRanges.length; index++) {
            if (position.dynamicWeight.weightPriceRanges[index].priceMax <= lastPriceMax) {
              position.dynamicWeight.weightPriceRanges[index].priceMax = lastPriceMax + priceStep;
              lastPriceMax = lastPriceMax + priceStep;
            } else {
              break;
            }
          }
          state.position = instanceToPlain(position);
        }
      }
    },
    removeDynamicWeightPriceRange(state, action: PayloadAction<{ arrayIndex: number }>) {
      const arrayIndex = action.payload.arrayIndex;
      const position = plainToInstance(Position, state.position);
      if (position.dynamicWeight?.weightPriceRanges.length && position.dynamicWeight?.weightPriceRanges.length > arrayIndex) {
        position.dynamicWeight?.weightPriceRanges.splice(arrayIndex, 1);
        state.position = instanceToPlain(position);
      }
    },
    removeDynamicWeight(state) {
      const position = plainToInstance(Position, state.position);
      position.dynamicWeight = undefined;
      state.position = instanceToPlain(position);
    },
    setPositionEditing(state, action: PayloadAction<{ position: object | undefined, parent: object | undefined }>) {
      state.position = action.payload.position;
      state.parentPosition = action.payload.parent;
      state.upsertStatus = 'idle';
    },
    setSecuritySelected(state, action: PayloadAction<{ security: object | undefined }>) {
      state.securitySelected = action.payload.security;
    },
    resetSecurities(state) {
      if (state.fetchSecurityStatus !== 'pending') {
        state.securities = initialState.securities;
        state.securitySelected = initialState.securitySelected;
        state.fetchSecurityStatus = initialState.fetchSecurityStatus;
      }
    },
    resetLastPrice(state) {
      if (state.fetchLastPriceStatus !== 'pending') {
        state.lastPrice = initialState.lastPrice;
        state.fetchLastPriceStatus = initialState.fetchLastPriceStatus;
      }
    },
    resetState(state) {
      // Keep only Asset Types
      state = {
        ...initialState,
        assetTypes: state.assetTypes,
        fetchAssetTypeStatus: state.fetchAssetTypeStatus
      };
    }
  },
  extraReducers: builder => {
    builder
      .addCase(upsertPosition.pending, (state, action) => {
        state.upsertStatus = 'pending';
      })
      .addCase(upsertPosition.fulfilled, (state, action) => {
        state.upsertStatus = 'succeeded';
        state.position = undefined;
      })
      .addCase(upsertPosition.rejected, (state, action) => {
        state.upsertStatus = 'failed';
      })

      .addCase(fetchAssetTypes.pending, (state, action) => {
        state.fetchAssetTypeStatus = 'pending';
      })
      .addCase(fetchAssetTypes.fulfilled, (state, action) => {
        state.fetchAssetTypeStatus = 'succeeded';
        state.assetTypes = Object(action.payload);
      })
      .addCase(fetchAssetTypes.rejected, (state, action) => {
        state.fetchAssetTypeStatus = 'failed';
      })

      .addCase(fetchSecurities.pending, (state, action) => {
        state.fetchSecurityStatus = 'pending';
      })
      .addCase(fetchSecurities.fulfilled, (state, action) => {
        state.fetchSecurityStatus = 'succeeded';
        state.securities = Object(action.payload);
      })
      .addCase(fetchSecurities.rejected, (state, action) => {
        state.fetchSecurityStatus = 'failed';
      })

      .addCase(fetchLastPrice.pending, (state, action) => {
        state.fetchLastPriceStatus = 'pending';
      })
      .addCase(fetchLastPrice.fulfilled, (state, action) => {
        state.fetchLastPriceStatus = 'succeeded';
        state.lastPrice = Object(action.payload);
      })
      .addCase(fetchLastPrice.rejected, (state, action) => {
        state.fetchLastPriceStatus = 'failed';
      })
  }
})

// Export selectors
export const selectPositionEditing = createSelector([(state: RootState) => state.editPosition.position], (positionEditing) => {
  if (positionEditing) {
    return plainToInstance(Position, positionEditing);
  } else {
    return undefined;
  }
})
export const selectPositionEditingParent = createSelector([(state: RootState) => state.editPosition.parentPosition], (parent) => {
  if (parent) {
    return plainToInstance(PortfolioComposite, parent);
  } else {
    return undefined;
  }
})
export const selectIsAnyPositionEditing = createSelector([(state: RootState) => state.editPosition.position], (positionEditing) => {
  return positionEditing ? true : false;
})
export const selectAssetTypeIdsFundos = createSelector([(state: RootState) => state.editPosition.assetTypes], (assetTypes) => {
  const assetTypeNames = ['FUNDO DE AÇÃO', 'FUNDO CAMBIAL', 'FUNDO MULTIMERCADO', 'FUNDO DE RENDA FIXA', 'FUNDO DE PREVIDÊNCIA'];
  return plainToInstance(AssetType, assetTypes).filter(assetType => assetTypeNames.includes(assetType.name)).map(assetType => assetType.id ?? '');
})
export const selectAssetTypeIdsAcoes = createSelector([(state: RootState) => state.editPosition.assetTypes], (assetTypes) => {
  const assetTypeNames = ['ACAO PN', 'ACAO ON'];
  return plainToInstance(AssetType, assetTypes).filter(assetType => assetTypeNames.includes(assetType.name)).map(assetType => assetType.id ?? '');
})
export const selectAssetTypeIdsBDR = createSelector([(state: RootState) => state.editPosition.assetTypes], (assetTypes) => {
  const assetTypeNames = ['BDR'];
  return plainToInstance(AssetType, assetTypes).filter(assetType => assetTypeNames.includes(assetType.name)).map(assetType => assetType.id ?? '');
})
export const selectAssetTypeIdsFiiFiagro = createSelector([(state: RootState) => state.editPosition.assetTypes], (assetTypes) => {
  const assetTypeNames = ['FII', 'FIAGRO'];
  return plainToInstance(AssetType, assetTypes).filter(assetType => assetTypeNames.includes(assetType.name)).map(assetType => assetType.id ?? '');
})
export const selectAssetTypeIdsETF = createSelector([(state: RootState) => state.editPosition.assetTypes], (assetTypes) => {
  const assetTypeNames = ['ETF INDICE ACAO', 'ETF INDICE RENDA FIXA', 'ETF OUTROS'];
  return plainToInstance(AssetType, assetTypes).filter(assetType => assetTypeNames.includes(assetType.name)).map(assetType => assetType.id ?? '');
})
export const selectAssetTypeIdsTitulosGoverno = createSelector([(state: RootState) => state.editPosition.assetTypes], (assetTypes) => {
  const assetTypeNames = ['LTN', 'NTN-F', 'LFT', 'NTNB PRINC', 'NTN-B', 'NTN-B1', 'NTN-C'];
  return plainToInstance(AssetType, assetTypes).filter(assetType => assetTypeNames.includes(assetType.name)).map(assetType => assetType.id ?? '');
})
export const selectSecurities = createSelector([(state: RootState) => state.editPosition.securities], (securities) => {
  return plainToInstance(Security, securities);
})
export const selectSecuritySelected = createSelector([(state: RootState) => state.editPosition.securitySelected], (securitySelected) => {
  if (securitySelected) {
    return plainToInstance(Security, securitySelected);
  } else {
    return undefined;
  }
})
export const selectDynamicWeight = createSelector([(state: RootState) => state.editPosition.position], (positionEditing) => {
  if (positionEditing) {
    return plainToInstance(Position, positionEditing).dynamicWeight;
  } else {
    return undefined;
  }
})
export const selectLastPrice = createSelector([(state: RootState) => state.editPosition.lastPrice], (lastPrice) => {
  if (lastPrice) {
    return plainToInstance(LastPrice, lastPrice);
  } else {
    return undefined;
  }
})
export const selectUpsertPositionStatus = (state: RootState) => state.editPosition.upsertStatus;
export const selectFetchAssetTypeStatus = (state: RootState) => state.editPosition.fetchAssetTypeStatus;
export const selectFetchSecurityStatus = (state: RootState) => state.editPosition.fetchSecurityStatus;
export const selectFetchLastPriceStatus = (state: RootState) => state.editPosition.fetchLastPriceStatus;

// Export the auto-generated action creator with the same name
export const { addDynamicWeightPriceRange, updateDynamicWeightPriceRange, removeDynamicWeightPriceRange, removeDynamicWeight, setPositionEditing, setSecuritySelected, resetSecurities, resetLastPrice, resetState } = editPositionSlice.actions;

// Export the generated reducer function
export default editPositionSlice.reducer;